import apiService from './api';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    department: string;
    employeeCode: string;
    avatar: string | null;
    permissions: string[];
  };
}

export interface RefreshTokenResponse {
  access_token: string;
  refresh_token: string;
}

class AuthService {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiService.post<LoginResponse>('/auth/login', credentials);
    
    // Store tokens
    apiService.setToken(response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);
    localStorage.setItem('user', JSON.stringify(response.user));
    
    return response;
  }

  async logout(): Promise<void> {
    try {
      // Use raw fetch for logout since it returns 204 No Content
      const token = localStorage.getItem('access_token');
      if (token) {
        await fetch('/api/v1/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'x-tenant-id': 'afrilab',
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage regardless of API call success
      apiService.clearToken();
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  }

  async refreshToken(): Promise<RefreshTokenResponse> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiService.post<RefreshTokenResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    });

    // Update stored tokens
    apiService.setToken(response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);

    return response;
  }

  async getProfile(): Promise<LoginResponse['user']> {
    return apiService.get<LoginResponse['user']>('/auth/profile');
  }

  getCurrentUser(): LoginResponse['user'] | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }
}

export const authService = new AuthService();
export default authService; 